// Data hooks for the International Acca module. Domain folder mirrors
// src/super-acca/. DOCTRINE: never fabricate — on error/empty the
// view-model degrades to null/[] and the presentational pages render
// their honest-empty states.
//
// `getBrowserClient() as any`: the generated Database type predates
// the intl_acca_* migrations. Repo idiom for fresh tables (cf.
// src/pages/sika/SikaPortfolioPage.tsx, the prior useIntlAcca.ts).
// Return values are explicitly cast to the typed handoff interfaces
// so consumer-side type-safety is preserved.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getBrowserClient } from "../supabase/browser.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";

const ACTIVE_KEY = "strikza:intlacca:active-tournament";

function readActive(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

export interface IntlAccaTournamentsVM {
  loading: boolean;
  error: string | null;
  tournaments: IntlAccaTournament[];
  activeId: string | null;
  setActive: (id: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  refetch: () => Promise<void>;
}

export function resolveActiveTournamentId(
  rows: Pick<IntlAccaTournament, "id" | "status">[],
  previous: string | null,
): string | null {
  const active = rows.find((t) => t.status === "ACTIVE");
  if (previous && rows.some((t) => t.id === previous && (t.status === "ACTIVE" || !active))) {
    return previous;
  }
  return active?.id ?? rows[0]?.id ?? null;
}

export function useIntlAccaTournaments(): IntlAccaTournamentsVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<IntlAccaTournament[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => readActive());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await supabase
        .from("intl_acca_tournaments")
        .select("*")
        .order("start_date", { ascending: false });
      if (res.error) throw res.error;
      const rows = (res.data ?? []) as IntlAccaTournament[];
      setTournaments(rows);
      // Seed active context: persisted choice if still present, else the
      // first ACTIVE tournament, else the most recent row.
      setActiveId((prev) => resolveActiveTournamentId(rows, prev));
    } catch (e) {
      // Honest-empty: missing table / RLS error degrades to no list.
      setError(e instanceof Error ? e.message : String(e));
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const setActive = useCallback((id: string): void => {
    setActiveId(id);
    try {
      window.localStorage.setItem(ACTIVE_KEY, id);
    } catch {
      /* vendor storage may be unavailable; non-fatal */
    }
  }, []);

  return { loading, error, tournaments, activeId, setActive, navigate, refetch: fetchAll };
}
